import api from "../../services/api";
import { useState } from "react";

const Field = ({ label, value, fullWidth }) => (
  <div className={fullWidth ? "col-span-2" : ""}>
    <p className="text-[11px] font-semibold text-[#4C444E] uppercase tracking-wider mb-1.5">{label}</p>
    <p className="text-[15px] border-b border-[#E7E8E9] pb-2 font-bold text-[#34094D]">
      {value || <span className="text-[#CEC3D0]">—</span>}
    </p>
  </div>
);

function DocItem({ label, apiField }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (url || loading) return;
    setLoading(true);
    try {
      const res = await api.get(`/kyc/document/${apiField}`, { responseType: "blob" });
      const mime = res.headers["content-type"] || "";
      setUrl({ src: URL.createObjectURL(new Blob([res.data], { type: mime })), mime });
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-gray-50 border border-gray-100 rounded-xl overflow-hidden flex flex-col">
      <div
        className="h-14 bg-gradient-to-br from-indigo-50 to-purple-100 flex items-center justify-center cursor-pointer"
        onClick={load}
      >
        {loading
          ? <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
          : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#9b9bc8" strokeWidth="1.5"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
        }
      </div>
      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wide pt-2 px-2 text-center">{label}</p>
      <div className="py-2 text-center">
        {!url
          ? <button onClick={load} disabled={loading} className="text-[10px] font-semibold text-emerald-600 bg-transparent border-none cursor-pointer">{loading ? "Loading…" : "View"}</button>
          : <a href={url.src} target="_blank" rel="noopener noreferrer" className="text-[10px] font-semibold text-emerald-600">Open ↗</a>
        }
      </div>
      {url && (
        <a href={url.src} target="_blank" rel="noopener noreferrer" className="block border-t border-gray-100 p-1.5">
          {url.mime.startsWith("image")
            ? <img src={url.src} alt={label} className="w-full rounded object-cover" style={{ maxHeight: 100 }} />
            : <iframe src={url.src} title={label} className="w-full rounded" style={{ height: 100, border: "none" }} />
          }
        </a>
      )}
    </div>
  );
}

const DOC_ITEMS = [
  { label: "PAN Card",        apiField: "pan_file" },
  { label: "Aadhaar Front",   apiField: "aadhaar_front" },
  { label: "Aadhaar Back",    apiField: "aadhaar_back" },
  { label: "GST Certificate", apiField: "gst_certificate" },
  { label: "Selfie / Video",  apiField: "selfie_video" },
];

export default function AccountInfoCard({ kyc, user }) {
  if (!kyc) return null;

  const verifications = [
    { label: "Aadhaar",  ok: kyc.aadhaar?.verified,  at: kyc.aadhaar?.verified_at },
    { label: "PAN",      ok: kyc.pan?.verified,       at: kyc.pan?.verified_at },
    { label: "GST",      ok: kyc.gst?.verified,       at: kyc.gst?.verified_at },
    { label: "Liveness", ok: kyc.liveness?.verified,  at: kyc.liveness?.verified_at },
  ];

  return (
    <div className="space-y-5 mt-6">
      {/* Account Information */}
      <div className="bg-white rounded-[20px] shadow-[0px_8px_24px_0px_#4B23640A] overflow-hidden">
        <div className="px-6 py-4 flex items-center justify-between border-b border-[#F0EBF4]">
          <span className="text-[18px] font-bold text-[#34094D]">Account Information</span>
          <span className="text-[10px] font-bold text-[#BB8DD5] bg-[#4B2364] px-2.5 py-1 rounded-full uppercase tracking-wider">API Partner</span>
        </div>
        <div className="p-5 grid grid-cols-2 gap-x-8 gap-y-5">
          <Field label="Full Legal Name"  value={kyc.personal?.full_name  || user?.name} />
          <Field label="Date of Birth"    value={kyc.personal?.dob} />
          <Field label="Business Name"    value={kyc.personal?.business_name} />
          <Field label="Business Type"    value={kyc.personal?.business_type} />
          <Field label="PAN Number"       value={kyc.pan?.pan_number} />
          <Field label="GST Number"       value={kyc.gst?.gst_number} />
          <Field label="Email"            value={user?.email} />
          <Field label="Mobile"           value={user?.mobile} />
          <Field label="Business Address" value={kyc.gst?.business_address} fullWidth />
        </div>

        {/* Verification badges */}
        {/* <div className="px-5 pb-5 grid grid-cols-4 gap-3">
          {verifications.map(({ label, ok, at }) => (
            <div key={label} className={`rounded-[12px] px-3 py-2.5 border ${ok ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"}`}>
              <div className="flex items-center gap-1.5 mb-0.5">
                {ok
                  ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>
                }
                <span className={`text-[12px] font-bold ${ok ? "text-green-700" : "text-gray-400"}`}>{label}</span>
              </div>
              {ok && at && <p className="text-[10px] text-green-600">{new Date(at).toLocaleDateString("en-IN")}</p>}
            </div>
          ))}
        </div> */}
      </div>

      {/* Documents */}
      {kyc.documents_submitted && (
        <div className="bg-white rounded-[20px] shadow-[0px_8px_24px_0px_#4B23640A] overflow-hidden">
          <div className="px-5 py-4 border-b border-[#F0EBF4] flex items-center gap-2">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4B2364" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
            <span className="text-[15px] font-bold text-[#1A1A1A]">Verified Documents</span>
          </div>
          <div className="p-4 grid grid-cols-5 gap-3">
            {DOC_ITEMS.map((d) => <DocItem key={d.apiField} {...d} />)}
          </div>
        </div>
      )}
    </div>
  );
}
